import { API_BASE_URL } from './constants';

/**
 * Thin typed wrapper around fetch for calls to the AURA FastAPI backend.
 *
 * Auth: in Batch 4, a bearer token (session id issued by Convex) will be
 * injected by an interceptor. For now the client is public-only — health
 * checks, upload URLs, etc.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** If true, body is sent as FormData; otherwise JSON. */
  formData?: boolean;
}

async function request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal, formData = false } = options;

  const url = `${API_BASE_URL.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

  const finalHeaders: Record<string, string> = { ...headers };
  let finalBody: BodyInit | undefined;

  if (body !== undefined) {
    if (formData) {
      finalBody = body as FormData;
      // Do NOT set Content-Type; the browser sets the multipart boundary.
    } else {
      finalHeaders['Content-Type'] = 'application/json';
      finalBody = JSON.stringify(body);
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: finalHeaders,
      body: finalBody,
      signal,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    throw new ApiError(0, 'NETWORK_ERROR', message);
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await response.json().catch(() => null) : await response.text().catch(() => null);

  if (!response.ok) {
    const errBody = (payload ?? {}) as { code?: string; message?: string; detail?: string };
    throw new ApiError(
      response.status,
      errBody.code ?? `HTTP_${response.status}`,
      errBody.message ?? errBody.detail ?? response.statusText,
      payload,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  formData: <T>(path: string, data: FormData, options?: Omit<ApiRequestOptions, 'method' | 'body' | 'formData'>) =>
    request<T>(path, { ...options, method: 'POST', body: data, formData: true }),
};

/**
 * Health check — confirms the backend is reachable.
 * Consumed by a dev-only indicator in the header.
 */
export async function checkHealth(): Promise<{ status: string; version: string }> {
  return api.get<{ status: string; version: string }>('/health');
}

// ─────────────────────────────────────────────────────────────────────────
//  Pipeline + checkpoints (added in Batch 12)
// ─────────────────────────────────────────────────────────────────────────

function withBearer(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export interface CheckpointDecidePayload {
  job_id: string;
  output_id: string;
  decision: 'approve' | 'edit' | 'revise';
  edited_payload?: unknown;
  revise_comment?: string;
}

export interface CheckpointDecideResponse {
  accepted: boolean;
  job_state_changed: boolean;
  new_job_state: string | null;
  message: string | null;
}

export async function postCheckpointDecide(
  token: string,
  body: CheckpointDecidePayload,
): Promise<CheckpointDecideResponse> {
  return api.post<CheckpointDecideResponse>('/checkpoints/decide', body, {
    headers: withBearer(token),
  });
}

export interface OraClarificationPayload {
  job_id: string;
  output_id: string;
  answers: Record<string, string>;
}

export async function postOraClarification(
  token: string,
  body: OraClarificationPayload,
): Promise<CheckpointDecideResponse> {
  return api.post<CheckpointDecideResponse>('/checkpoints/ora-clarification', body, {
    headers: withBearer(token),
  });
}

export interface ResumeJobResponse {
  accepted: boolean;
  new_state: string;
  message: string | null;
}

export async function postJobResume(token: string, jobId: string): Promise<ResumeJobResponse> {
  return api.post<ResumeJobResponse>(`/jobs/${jobId}/resume`, undefined, {
    headers: withBearer(token),
  });
}

export async function postJobRetry(token: string, jobId: string): Promise<ResumeJobResponse> {
  return api.post<ResumeJobResponse>(`/jobs/${jobId}/retry`, undefined, {
    headers: withBearer(token),
  });
}
